import { useState } from 'react';
import { Box, Button, Divider, IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { formatSize, getMimeType, isPreviewable } from './file-utils';
import FileIcon from './FileIcon';

function formatDateString(dateString) {
    let date = new Date(dateString);
    return date.toLocaleString();
}

// Menu titik tiga per baris. Di HP ini satu-satunya kolom aksi (kolom
// Preview/Share/Download/Delete terpisah tidak muat), dan juga pengganti
// drag & drop (yang tidak jalan di layar sentuh) lewat "Pindahkan ke...".
function RowActionsMenu({ params, currentAction, trashMode, handlers }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const close = () => setAnchorEl(null);
    const busy = currentAction !== "";
    const row = params.row;
    const isDirectory = row.type === 'directory';
    const run = (fn) => () => {
        close();
        fn(params);
    };

    return (
        <>
            <IconButton
                size="small"
                disabled={busy}
                aria-label="Aksi lainnya"
                onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={close} onClick={(e) => e.stopPropagation()}>
                {!trashMode && ([
                    <MenuItem key="preview" disabled={isDirectory || !isPreviewable(row.name)} onClick={run(handlers.onPreview)}>
                        <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                        Preview
                    </MenuItem>,
                    <MenuItem key="share" disabled={isDirectory} onClick={run(handlers.onShare)}>
                        <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
                        Bagikan
                    </MenuItem>,
                    <MenuItem key="download" onClick={run(handlers.onDownload)}>
                        <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                        Download
                    </MenuItem>,
                    isDirectory && (
                        <MenuItem key="zip" onClick={run(handlers.onDownloadZip)}>
                            <ListItemIcon><FolderZipIcon fontSize="small" /></ListItemIcon>
                            Download sebagai ZIP
                        </MenuItem>
                    ),
                    <Divider key="d1" />,
                    <MenuItem key="move" onClick={run(handlers.onMove)}>
                        <ListItemIcon><DriveFileMoveIcon fontSize="small" /></ListItemIcon>
                        Pindahkan ke...
                    </MenuItem>,
                    <MenuItem key="rename" onClick={run(handlers.onRename)}>
                        <ListItemIcon><DriveFileRenameOutlineIcon fontSize="small" /></ListItemIcon>
                        Ganti nama
                    </MenuItem>,
                    <MenuItem key="star" onClick={run(handlers.onToggleStar)}>
                        <ListItemIcon>{row.starred ? <StarIcon fontSize="small" sx={{ color: '#f4b400' }} /> : <StarBorderIcon fontSize="small" />}</ListItemIcon>
                        {row.starred ? 'Hapus bintang' : 'Beri bintang'}
                    </MenuItem>,
                    <Divider key="d2" />,
                    <MenuItem key="delete" onClick={run(handlers.onDelete)}>
                        <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                        Hapus
                    </MenuItem>,
                ].filter(Boolean))}
                {trashMode && ([
                    <MenuItem key="restore" onClick={run(handlers.onRestore)}>
                        <ListItemIcon><RestoreFromTrashIcon fontSize="small" /></ListItemIcon>
                        Restore
                    </MenuItem>,
                    <MenuItem key="deleteForever" onClick={run(handlers.onPermanentDelete)}>
                        <ListItemIcon><DeleteForeverIcon fontSize="small" color="error" /></ListItemIcon>
                        Hapus permanen
                    </MenuItem>,
                ])}
            </Menu>
        </>
    );
}

function parentOf(path) {
    if (!path) return '/';
    const index = path.lastIndexOf('/');
    return index <= 0 ? '/' : path.slice(0, index);
}

// options: { currentAction, trashMode, isMobile, showLocation, handlers }
// handlers: onPreview, onShare, onDownload, onDownloadZip, onDelete, onRestore,
//           onPermanentDelete, onMove, onRename, onToggleStar, onOpenLocation (semua menerima params DataGrid)
export default function buildColumns({ currentAction, trashMode = false, isMobile = false, showLocation = false, handlers }) {
    const typeColumn = {
        field: 'type',
        headerName: '',
        width: 0,
        renderCell: (params) => (
            <FileIcon mimeType={params.row.type === "directory" ? "inode/directory" : getMimeType(params.row.name)} />
        ),
        disableColumnMenu: true,
        sortable: false,
        headerClassName: 'no-width no-seperator',
        cellClassName: 'icon-cell'
    };

    // Folder selalu di atas file, lalu alfabetis (angka di nama file
    // diurutkan wajar, "file2" sebelum "file10").
    const nameSortComparator = (v1, v2, cell1, cell2) => {
        const row1 = cell1.api.getRow(cell1.id);
        const row2 = cell2.api.getRow(cell2.id);
        const dirDiff = (row2.type === 'directory' ? 1 : 0) - (row1.type === 'directory' ? 1 : 0);
        if (dirDiff !== 0) return dirDiff;
        return String(v1).localeCompare(String(v2), undefined, { numeric: true, sensitivity: 'base' });
    };

    const renderName = (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.25, py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={params.value}>{params.value}</Box>
                {params.row.starred && <StarIcon sx={{ fontSize: 15, color: '#f4b400', flexShrink: 0 }} />}
            </Box>
            {showLocation && (
                // Klik lokasi = buka folder tempat item itu (file-nya langsung dipilih).
                <Typography variant="caption" color="text.secondary" noWrap title={`Buka folder ${parentOf(params.row.path)}`}
                    onClick={handlers.onOpenLocation ? (e) => { e.stopPropagation(); handlers.onOpenLocation(params); } : undefined}
                    onDoubleClick={(e) => e.stopPropagation()}
                    sx={handlers.onOpenLocation ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: 'primary.main' } } : undefined}>
                    {parentOf(params.row.path)}
                </Typography>
            )}
        </Box>
    );

    const nameColumn = {
        field: 'name',
        headerName: 'Name',
        // Rename lewat klik-tarik di sel dimatikan di dalam Trash - dari
        // sana pakai tombol Restore (mengembalikan nama aslinya) saja.
        editable: !trashMode,
        headerClassName: 'extra-width',
        sortComparator: nameSortComparator,
        renderCell: renderName,
    };

    const actionsColumn = {
        field: 'actions',
        headerName: '',
        width: 48,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
            <RowActionsMenu params={params} currentAction={currentAction} trashMode={trashMode} handlers={handlers} />
        ),
    };

    if (isMobile) {
        return [
            typeColumn,
            { ...nameColumn, flex: 1, minWidth: 120 },
            {
                field: 'size',
                headerName: '',
                width: 64,
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params) => formatSize(params.row.size),
            },
            actionsColumn,
        ];
    }

    const textButton = (field, headerName, width, label, onClick, disabledFor, color = 'primary') => ({
        field,
        headerName,
        width,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
            <Button
                disabled={currentAction !== "" || (disabledFor ? disabledFor(params.row) : false)}
                variant="text"
                color={color}
                onClick={() => onClick(params)}
            >
                {label}
            </Button>
        ),
    });

    // Kolom menu titik tiga diletakkan tepat setelah nama: tabel di desktop
    // lebih lebar dari layar, dan DataGrid tidak merender kolom yang di luar
    // layar - kalau ditaruh paling kanan, menunya baru kelihatan setelah
    // tabel digeser ke samping.
    return [
        typeColumn,
        { ...nameColumn, width: 400 },
        actionsColumn,
        {
            field: 'enc',
            headerName: '',
            width: 40,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                params.row.enc ? <span title="Terenkripsi">🔒</span> : null
            ),
        },
        {
            field: 'created_at',
            headerName: 'Created at',
            type: 'date',
            width: 190,
            valueFormatter: (params) => formatDateString(params.value),
        },
        {
            field: 'updated_at',
            headerName: 'Updated at',
            type: 'date',
            width: 190,
            valueFormatter: (params) => formatDateString(params.value),
        },
        {
            field: 'size',
            headerName: 'Size',
            width: 110,
            valueFormatter: (params) => formatSize(params.value),
        },
        textButton('preview', 'Preview', 90, 'Preview', handlers.onPreview, (row) => row.type === 'directory' || !isPreviewable(row.name)),
        ...(trashMode ? [
            textButton('restore', 'Restore', 90, 'Restore', handlers.onRestore),
            textButton('deleteForever', 'Delete Forever', 130, 'Delete Forever', handlers.onPermanentDelete, null, 'error'),
        ] : [
            textButton('share', 'Share', 85, 'Share', handlers.onShare, (row) => row.type === 'directory'),
            textButton('download', 'Download', 110, 'Download', handlers.onDownload),
            textButton('delete', 'Delete', 85, 'Delete', handlers.onDelete, null, 'error'),
        ]),
    ];
}
